/* eslint-disable indent */
/* eslint-disable @typescript-eslint/camelcase */
import {
  FormControlLabel,
  Grid,
  makeStyles,
  Switch,
  Typography,
} from '@material-ui/core';
import {
  ExpandMore as ExpandMoreIcon,
  ThumbUp as ThumbUpIcon,
} from '@material-ui/icons';
import clsx from 'clsx';
import Button from 'components/Button';
import { SearchBar } from 'components/SearchBar';
import { getAllPartnersRequest } from 'modules/partners/actions';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import { Formik } from 'formik';
import { useTableFilters } from 'hooks/useTableFilters';
import i18n from 'i18n';
import {
  ListPartnerDataProps,
  PartnerFields,
} from 'modules/partners/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import GenericForm from 'skyone-lib/lib/GenericForm';
import { applyI18n, regexExp } from 'utils/helpers';
import ApplicationState from 'store/interfaces';
import {
  OperatorTokensProps,
  OperatorTokenFields,
} from 'modules/operators/interfaces';
import { useParams } from 'react-router';
import FormAllowedIps from './FormAllowedIps';
import { initialValues } from './initialValues';
import validationSchema from './schema';
import screenFields from './screenFields';
import styles from './styles';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 10,
  search: '',
};

interface SwitchComponentProps {
  id?: string;
  label: string | React.ReactElement;
  isChecked: boolean;
  children?: React.ReactNode;
  hidden?: boolean;
  onChange: (e: any, value: any) => void;
}

const SwitchComponent = ({
  id,
  label,
  isChecked,
  children,
  hidden,
  onChange,
}: SwitchComponentProps) => {
  const classes = useStyles();

  return hidden ? null : (
    <Grid item container spacing={2}>
      <Grid item xs={12}>
        <FormControlLabel
          classes={{ label: classes.switchLabel, root: classes.switchRoot }}
          control={
            <Switch
              id={id}
              checked={isChecked}
              onChange={onChange}
              classes={{ colorSecondary: classes.switchChecked }}
              size="small"
            />
          }
          label={label}
        />
      </Grid>
      {!!children && isChecked && children}
    </Grid>
  );
};

interface FormTokenProps {
  defaultValues?: OperatorTokensProps;
  isLoading: boolean;
  partnerList: ListPartnerDataProps;
  getAllPartnersRequest: (props) => void;
  onSubmit: (values) => void;
  onCancel: () => void;
}

const FormToken = ({
  defaultValues,
  isLoading,
  partnerList,
  getAllPartnersRequest,
  onSubmit,
  onCancel,
}: FormTokenProps) => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const {
    query,
    handleLimitChange,
    handleOrder,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const [values, setValues] = React.useState<OperatorTokenFields | null>(
    initialValues
  );
  const [openPartnerSelector, setOpenPartnerSelector] = React.useState(
    !defaultValues
  );
  const [partnerToken, setPartnerToken] = React.useState({
    name: defaultValues?.partner?.name,
    uuid: defaultValues?.partner?.uuid,
  });
  const [hasInvalidIPs, setHasInvalidIPs] = React.useState(false);

  const handleSubmit = values => {
    onSubmit({
      ...values,
      partner_uuid: partnerToken?.uuid,
      expiration_date: new Date(values.expiration_date).toISOString(),
      valid_ips:
        values.valid_ips.length === 0
          ? ''
          : values.valid_ips
              .filter((ip: string) => regexExp.numbersDotsBarsOnly.test(ip))
              .join(','),
    });
  };

  React.useEffect(() => {
    getAllPartnersRequest({
      query: {
        ...query,
        operator_uuid: id,
      },
    });
  }, [id, query, getAllPartnersRequest]);

  React.useEffect(() => {
    if (defaultValues) {
      const {
        description,
        expiration_date,
        partner,
        filter_using_ips,
        valid_ips,
      } = defaultValues;

      setValues({
        description,
        expiration_date,
        partner_uuid: partner.uuid,
        filter_using_ips,
        valid_ips: valid_ips.split(','),
      });
    }
  }, [defaultValues]);

  return (
    <Formik
      initialValues={values || initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ setFieldValue, isValid, values, errors }) => (
        <Grid container spacing={2}>
          <GenericForm
            fields={values}
            screenFields={applyI18n(screenFields.slice(0, 1), i18n.language)}
            setFieldValue={setFieldValue}
            errors={errors}
          />

          <Grid item xs={12}>
            <GenericForm
              fields={values}
              screenFields={applyI18n(screenFields.slice(1, 2), i18n.language)}
              setFieldValue={setFieldValue}
              errors={errors}
            />
          </Grid>
          <Grid
            container
            item
            className={clsx(classes.titleGrid, {
              [classes.titleGridError]: !partnerToken?.uuid,
            })}
          >
            <Grid container alignItems="center">
              <Typography className={classes.text}>{`${i18n.t(
                'PARTNER_KEY'
              )}:`}</Typography>

              <Grid item>
                <Button
                  id="btn-select-partner"
                  onClick={() => setOpenPartnerSelector(!openPartnerSelector)}
                  endIcon={<ExpandMoreIcon />}
                >
                  {partnerToken?.name ? (
                    <Typography className={classes.boldText}>
                      {partnerToken?.name}
                    </Typography>
                  ) : (
                    <Typography className={classes.boldText}>
                      {i18n.t('CHOOSE_PARTNER_KEY')}
                    </Typography>
                  )}
                </Button>
              </Grid>
            </Grid>

            {openPartnerSelector && (
              <Grid className={classes.partnersTable}>
                <SearchBar
                  id="search-clients"
                  isLoading={isLoading}
                  handleSearch={handleSearch}
                  setSearchValue={setSearchValue}
                />
                <TableItems
                  header={
                    <>
                      <TableCell>
                        <></>
                      </TableCell>
                      <TableCell
                        key="name"
                        order
                        handleOrder={() => handleOrder('name')}
                      >
                        {i18n.t('PARTNER_NAME_KEY')}
                      </TableCell>
                    </>
                  }
                  renderItems={(item: PartnerFields) => (
                    <TableRow
                      key={item?.partner_uuid}
                      onClick={() => {
                        setPartnerToken({
                          name: item?.name,
                          uuid: item?.partner_uuid,
                        });
                        setOpenPartnerSelector(false);
                      }}
                      className={clsx(classes.tableRow, {
                        [classes.partnerSelected]:
                          partnerToken?.uuid === item?.partner_uuid,
                      })}
                    >
                      <TableCell className={classes.likeCell}>
                        {partnerToken?.uuid === item?.partner_uuid ? (
                          <ThumbUpIcon fontSize="small" />
                        ) : (
                          <></>
                        )}
                      </TableCell>
                      <TableCell className={classes.partnerCell}>
                        <Typography>{item?.name}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  data={partnerList}
                  isLoading={isLoading}
                  onChangePage={handlePageChange}
                  onChangeLimit={handleLimitChange}
                  overrideRowsCount={query.limit}
                  notFoundText={i18n.t('NO_PARTNER_KEY')}
                />
              </Grid>
            )}
          </Grid>
          <Grid item container>
            <SwitchComponent
              id="toggle-block-multiple-sessions"
              label={
                <Typography className={classes.filterIP}>
                  {i18n.t('OPERATORS_PAGE.FILTER_TO_IPS')}
                </Typography>
              }
              onChange={(e, value) => setFieldValue('filter_using_ips', value)}
              isChecked={values.filter_using_ips}
            >
              <Grid item xs={12}>
                <FormAllowedIps
                  onChange={value => {
                    setFieldValue('valid_ips', value);

                    setHasInvalidIPs(
                      value?.some(ip => !regexExp.ipNumber.test(ip))
                    );
                  }}
                  onError={() => null}
                  value={
                    typeof values?.valid_ips === 'string'
                      ? [values?.valid_ips]
                      : values?.valid_ips
                  }
                  active={values.filter_using_ips}
                />
              </Grid>
            </SwitchComponent>
          </Grid>

          <Grid container justify="flex-end" className={classes.buttonsBase}>
            <Grid container className={classes.buttonsAlign}>
              <Button
                id="submit-button"
                submitButton
                onClick={() => handleSubmit(values)}
                disabled={!partnerToken?.uuid || !isValid || hasInvalidIPs}
              >
                {i18n.t('SAVE_KEY')}
              </Button>
              <Button id="cancel-button" variant="outlined" onClick={onCancel}>
                {i18n.t('CANCEL_KEY')}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Formik>
  );
};

const mapStateToProps = ({
  partners: { partnerList, isLoading },
  operators: {
    operatorTokens: { loading },
  },
}: ApplicationState) => ({
  partnerList,
  loading,
  isLoading,
});

export default connect(mapStateToProps, {
  getAllPartnersRequest,
})(FormToken);
